import { discountForStore, editorItemKey, type EditorItem } from "./wholesale-items";

/** Korpa prezentacijskog moda: partner (nosilac rabata) i stavke u obliku uređivača
 *  narudžbe, pa `toItemInputs` i `editorTotals` rade nad njom bez adaptera. */
export type ShowroomCart = { partnerId: string | null; items: EditorItem[] };

export const EMPTY_CART: ShowroomCart = { partnerId: null, items: [] };

export const CART_STORAGE_KEY = "komoran.showroom.cart";

const num = (v: unknown, fallback = 0): number => (typeof v === "number" && Number.isFinite(v) ? v : fallback);
const str = (v: unknown): string => (typeof v === "string" ? v : "");

/** Jedna stavka iz spremljenog JSON-a. Vraća `null` za sve što se ne može pouzdano
 *  pročitati — korpa s terena starija od zadnjeg deploya ne smije srušiti ekran. */
function reviveItem(raw: any): EditorItem | null {
  if (!raw || typeof raw !== "object") return null;
  const storeId = str(raw.storeId);
  const wcProductId = num(raw.wcProductId, 0);
  if (!storeId || !Number.isInteger(wcProductId) || wcProductId <= 0) return null;
  const quantity = num(raw.quantity, 0);
  if (!Number.isInteger(quantity) || quantity < 1) return null;
  const wcVariationId = raw.wcVariationId === null || raw.wcVariationId === undefined ? null : num(raw.wcVariationId, 0);
  if (wcVariationId !== null && (!Number.isInteger(wcVariationId) || wcVariationId <= 0)) return null;
  return {
    storeId,
    storeName: str(raw.storeName),
    wcProductId,
    wcVariationId,
    sku: str(raw.sku),
    name: str(raw.name),
    imageUrl: str(raw.imageUrl),
    quantity,
    listPrice: Math.max(0, num(raw.listPrice, 0)),
    discountPct: Math.min(100, Math.max(0, num(raw.discountPct, 0))),
    overridePrice: null,
    stockQuantity: raw.stockQuantity === null || raw.stockQuantity === undefined ? null : num(raw.stockQuantity, 0),
  };
}

/** Spremljeni JSON → korpa. Nevaljao unos daje praznu korpu, nikad izuzetak. */
export function loadCart(raw: string | null | undefined): ShowroomCart {
  if (!raw) return EMPTY_CART;
  let parsed: any;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return EMPTY_CART;
  }
  if (!parsed || typeof parsed !== "object" || !Array.isArray(parsed.items)) return EMPTY_CART;
  const items = parsed.items.map(reviveItem).filter((i: EditorItem | null): i is EditorItem => i !== null);
  const partnerId = typeof parsed.partnerId === "string" && parsed.partnerId ? parsed.partnerId : null;
  return { partnerId, items };
}

export function saveCart(cart: ShowroomCart): string {
  return JSON.stringify({ partnerId: cart.partnerId, items: cart.items });
}

/** Dodavanje iz kataloga: isti ključ zbraja količinu, novi ključ ide na kraj liste. */
export function addToCart(cart: ShowroomCart, item: EditorItem): ShowroomCart {
  const key = editorItemKey(item);
  const qty = Math.max(1, Math.round(num(item.quantity, 1)));
  if (cart.items.some((i) => editorItemKey(i) === key)) {
    return {
      ...cart,
      items: cart.items.map((i) => (editorItemKey(i) === key ? { ...i, quantity: i.quantity + qty } : i)),
    };
  }
  return { ...cart, items: [...cart.items, { ...item, quantity: qty }] };
}

/** Postavljanje količine; nula (ili manje) briše stavku, decimale se zaokružuju. */
export function setQuantity(cart: ShowroomCart, key: string, quantity: number): ShowroomCart {
  const qty = Math.round(num(quantity, 0));
  if (qty < 1) return removeFromCart(cart, key);
  return { ...cart, items: cart.items.map((i) => (editorItemKey(i) === key ? { ...i, quantity: qty } : i)) };
}

export function removeFromCart(cart: ShowroomCart, key: string): ShowroomCart {
  return { ...cart, items: cart.items.filter((i) => editorItemKey(i) !== key) };
}

/** Novi partner → novi rabat na svakoj stavci, po shopu ako partner ima poseban. */
export function repriceCart(
  cart: ShowroomCart,
  partnerDiscount: number,
  storeDiscounts?: { storeId: string; discountPct: number }[],
): ShowroomCart {
  return {
    ...cart,
    items: cart.items.map((i) => ({ ...i, discountPct: discountForStore(i.storeId, partnerDiscount, storeDiscounts) })),
  };
}

/** Ukupan broj komada u korpi — brojka na donjoj traci. */
export function cartCount(cart: ShowroomCart): number {
  return cart.items.reduce((n, i) => n + i.quantity, 0);
}

/** Količina jednog artikla u korpi; 0 kad ga nema. Kartica po ovome crta oznaku. */
export function quantityOf(cart: ShowroomCart, key: string): number {
  return cart.items.find((i) => editorItemKey(i) === key)?.quantity ?? 0;
}

/** Zbir svih varijacija jednog proizvoda — kartica izloga je krovna, pa njena oznaka
 *  mora pokazati sve što je iz tog proizvoda otišlo u korpu, ne samo jednu varijaciju. */
export function quantityOfProduct(cart: ShowroomCart, storeId: string, wcProductId: number): number {
  return cart.items.reduce((n, i) => (i.storeId === storeId && i.wcProductId === wcProductId ? n + i.quantity : n), 0);
}
